import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface AchievementView {
  id: string;
  display_name: string;
  description: string;
  category: string;
  hidden: boolean;
  visible: boolean;
  current: number;
  target: number;
  completed_tiers: number[];
  unclaimed_tiers: number[];
  complete: boolean;
}

export default function Achievements({ session }: Props) {
  const playerId = session.playerId;
  const [achievements, setAchievements] = useState<AchievementView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [eventType, setEventType] = useState('');
  const [eventTag, setEventTag] = useState('');

  const base = `/api/v1/games/${session.gameId}/modules/achievement/achievements/player/${playerId}`;

  const reload = () => {
    fetch(base)
      .then(r => r.json())
      .then((d: { achievements: AchievementView[] }) => setAchievements(d.achievements))
      .catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId, playerId]);

  const fireEvent = async () => {
    if (!eventType.trim()) return;
    setBusy(true); setError(null);
    try {
      const body: Record<string, string> = { event_type: eventType.trim() };
      if (eventTag.trim()) body.event_tag = eventTag.trim();
      const res = await fetch(`${base}/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const claim = async (achievementId: string, tier: number) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/claim/${achievementId}/${tier}`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const pct = (a: AchievementView) => a.target > 0 ? Math.min(100, Math.round((a.current / a.target) * 100)) : 0;

  return (
    <Container>
      <PageHeader
        title="Achievements"
        subtitle="Track milestones, fire events, claim rewards."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Event Trigger */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Fire Event</h3>
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Event Type</label>
            <input className="input" placeholder="entity_killed" value={eventType} onChange={e => setEventType(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Tag (optional)</label>
            <input className="input" placeholder="goblin" value={eventTag} onChange={e => setEventTag(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !eventType.trim()} onClick={fireEvent}>Fire</button>
        </div>
      </div>

      {/* Achievement List */}
      {achievements.length > 0 && (
        <div className="grid gap-3">
          {achievements.map(a => (
            <div key={a.id} className={`card p-4 ${!a.visible ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{a.visible ? a.display_name : '???'}</span>
                    <span className="badge badge-muted">{a.category}</span>
                    {a.hidden && <span className="badge badge-warn">hidden</span>}
                    {a.complete && <span className="badge badge-ok">complete</span>}
                  </div>
                  <p className="text-xs text-fg-muted mb-2">
                    {a.visible ? (a.description || a.id) : 'Hidden achievement'}
                  </p>
                  {/* Progress bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-bg-2 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-brand transition-all"
                        style={{ width: `${pct(a)}%` }}
                      />
                    </div>
                    <span className="text-xs text-fg-muted whitespace-nowrap">
                      {a.current}/{a.target} ({pct(a)}%)
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {a.unclaimed_tiers.map(t => (
                    <button
                      key={t}
                      className="btn btn-primary btn-sm"
                      disabled={busy}
                      onClick={() => claim(a.id, t)}
                    >
                      Claim T{t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {achievements.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No achievements published</div>
          <div className="text-xs text-fg-muted">Create achievements in Config Explorer → Achievement, publish, then manage here.</div>
        </div>
      )}
    </Container>
  );
}
